import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { extractErrorMessage } from '@/lib/errors';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { t } = useTranslation();

  const sessionId = searchParams.get('session_id');
  const isNewUser = searchParams.get('isNewUser') === 'true';

  const [state, setState] = useState('verifying');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!sessionId) {
        setState('error');
        setError(t('subscriptionSuccess.errorNoSession'));
        return;
      }
      try {
        const resp = await api.post(`/stripe/verify-session/${sessionId}`);
        if (cancelled) return;
        if (resp.data?.success) {
          const updated = resp.data.user;
          if (updated) updateUser?.(updated);
          setState('success');
          setTimeout(() => {
            navigate(isNewUser ? '/dashboard' : '/dashboard', { replace: true });
          }, 1800);
        } else {
          setState('error');
          setError(resp.data?.error || t('subscriptionSuccess.errorNotVerified'));
        }
      } catch (e) {
        if (cancelled) return;
        setState('error');
        setError(extractErrorMessage(e, t('subscriptionSuccess.errorFailed')));
      }
    }

    verify();
    return () => { cancelled = true; };
  }, [sessionId, isNewUser, navigate, updateUser, t]);

  const renderBody = () => {
    if (state === 'verifying') {
      return (
        <>
          <Loader2 className="w-12 h-12 animate-spin text-farm-primary mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">{t('subscriptionSuccess.verifyingTitle')}</h1>
          <p className="text-muted-foreground text-sm">{t('subscriptionSuccess.verifyingBody')}</p>
        </>
      );
    }
    if (state === 'success') {
      return (
        <>
          <CheckCircle2 className="w-14 h-14 text-farm-success mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">{t('subscriptionSuccess.successTitle')}</h1>
          <p className="text-muted-foreground text-sm">{t('subscriptionSuccess.successBody')}</p>
        </>
      );
    }
    return (
      <>
        <XCircle className="w-14 h-14 text-destructive mx-auto mb-4" />
        <h1 className="font-heading text-xl font-bold mb-2">{t('subscriptionSuccess.errorTitle')}</h1>
        <p className="text-sm text-muted-foreground mb-6">{error}</p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => navigate('/subscription/plans', { replace: true })}>
            {t('subscriptionSuccess.backToPlans')}
          </Button>
          <Button onClick={() => navigate('/dashboard', { replace: true })}>
            {t('subscriptionSuccess.goToDashboard')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="p-6 min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md w-full text-center">
        {renderBody()}
      </div>
    </div>
  );
}
